import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { cn } from "@/lib/utils";

export type PaginationProps = {
  /** Current page (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items */
  totalCount: number;
  /** Current page size */
  pageSize: number;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Start index for display (1-indexed) */
  startIndex: number;
  /** End index for display */
  endIndex: number;
  /** Whether previous navigation is enabled */
  canGoPrev: boolean;
  /** Whether next navigation is enabled */
  canGoNext: boolean;
  /** Go to specific page */
  onPageChange: (page: number) => void;
  /** Change page size */
  onPageSizeChange: (size: number) => void;
  /** Go to first page */
  onFirstPage: () => void;
  /** Go to last page */
  onLastPage: () => void;
  /** Go to previous page */
  onPrevPage: () => void;
  /** Go to next page */
  onNextPage: () => void;
  /** Additional className */
  className?: string;
  /** Show page size selector */
  showPageSize?: boolean;
  /** Show "Showing X-Y of Z" text */
  showSummary?: boolean;
  /** Show first/last buttons */
  showFirstLast?: boolean;
};

/**
 * Pagination component for navigating through paginated data
 *
 * @example
 * ```tsx
 * <Pagination
 *   page={pagination.page}
 *   totalPages={pagination.totalPages}
 *   totalCount={pagination.totalCount}
 *   pageSize={pagination.pageSize}
 *   pageSizeOptions={pageSizeOptions}
 *   startIndex={startIndex}
 *   endIndex={endIndex}
 *   canGoPrev={canGoPrev}
 *   canGoNext={canGoNext}
 *   onPageChange={goToPage}
 *   onPageSizeChange={setPageSize}
 *   onFirstPage={firstPage}
 *   onLastPage={lastPage}
 *   onPrevPage={prevPage}
 *   onNextPage={nextPage}
 * />
 * ```
 */
export function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  pageSizeOptions = [25, 50, 100, 200],
  startIndex,
  endIndex,
  canGoPrev,
  canGoNext,
  onPageChange,
  onPageSizeChange,
  onFirstPage,
  onLastPage,
  onPrevPage,
  onNextPage,
  className,
  showPageSize = true,
  showSummary = true,
  showFirstLast = true,
}: PaginationProps) {
  // Generate page numbers to show
  const getPageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      // Adjust if at the edges
      if (page <= 3) {
        end = Math.min(totalPages - 1, 4);
      } else if (page >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }

      // Add ellipsis before middle section if needed
      if (start > 2) {
        pages.push("ellipsis");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis after middle section if needed
      if (end < totalPages - 1) {
        pages.push("ellipsis");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-between gap-4 sm:flex-row",
        className,
      )}
    >
      {/* Summary text */}
      {showSummary && (
        <div className="text-sm text-slate-600">
          Showing <span className="font-medium">{startIndex}</span> to{" "}
          <span className="font-medium">{endIndex}</span> of{" "}
          <span className="font-medium">{totalCount.toLocaleString()}</span>{" "}
          results
        </div>
      )}

      {/* Navigation controls */}
      <div className="flex items-center gap-2">
        {/* Page size selector */}
        {showPageSize && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Rows:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          {/* First page */}
          {showFirstLast && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onFirstPage}
              disabled={!canGoPrev}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Previous page */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onPrevPage}
            disabled={!canGoPrev}
            aria-label="Go to previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {pageNumbers.map((pageNum, idx) =>
              pageNum === "ellipsis" ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 text-sm text-slate-400"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(pageNum)}
                  aria-label={`Go to page ${pageNum}`}
                  aria-current={pageNum === page ? "page" : undefined}
                >
                  {pageNum}
                </Button>
              ),
            )}
          </div>

          {/* Next page */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onNextPage}
            disabled={!canGoNext}
            aria-label="Go to next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page */}
          {showFirstLast && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onLastPage}
              disabled={!canGoNext}
              aria-label="Go to last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Simple pagination controls without page numbers
 * Use when you just need prev/next navigation
 */
export function SimplePagination({
  page,
  totalPages,
  canGoPrev,
  canGoNext,
  onPrevPage,
  onNextPage,
  className,
}: Pick<
  PaginationProps,
  | "page"
  | "totalPages"
  | "canGoPrev"
  | "canGoNext"
  | "onPrevPage"
  | "onNextPage"
  | "className"
>) {
  return (
    <div className={cn("flex items-center justify-center gap-4", className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={onPrevPage}
        disabled={!canGoPrev}
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Previous
      </Button>

      <span className="text-sm text-slate-600">
        Page {page} of {totalPages}
      </span>

      <Button
        variant="outline"
        size="sm"
        onClick={onNextPage}
        disabled={!canGoNext}
      >
        Next
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}
